import type { MechanicDefinition } from "./types";

export type InventoryScope = "player" | "party" | "group" | "stash" | "location" | "npc";

export function inventoryStateKey(scope: InventoryScope): string {
  if (scope === "player") return "player_inventory";
  return `${scope}_inventory`;
}

export const inventory: MechanicDefinition = {
  id: "inventory",
  name: "Inventory",
  description: "Contextual, tag-based inventory. Items are narrative objects with tags and categories — stats are optional. Supports player, party, org, faction, stash, and location-based storage.",
  icon: "Backpack",
  category: "systems",
  aiHints: {
    purpose: "Tracks items contextually across multiple scopes. Items don't need hard stats — a knife is narratively dangerous without a DMG number. Tags and categories drive organization; the AI determines narrative consequences.",
    whenToUse: "Any world where item tracking matters — loot-driven, survival, RPG, adventure, strategy, or narrative games.",
    synergies: ["combat", "skill_checks", "economy", "survival", "character_classes", "organization", "resources", "territory"],
    conflicts: [],
    prerequisites: [],
    tags: ["items", "equipment", "loot", "weight", "gear", "shared inventory", "contextual", "narrative", "tags"],
    complexity: 3,
    exampleConfig: { weight_limit: 0, categorized: true, equipment_slots: true, contextual_items: true, enable_party_inventory: true, enable_location_storage: true },
    genrePresets: {
      "classic_dnd": { weight_limit: 50, str_scaling: "dnd_style", equipment_slots: true, item_modifiers: true, cursed_items: true, enable_party_inventory: true },
      "survival": { weight_limit: 30, str_scaling: "additive", durability: true, equipment_slots: true, enable_party_inventory: true },
      "narrative_light": { weight_limit: 0, str_scaling: "none", equipment_slots: false, item_modifiers: false, contextual_items: true },
      "strategy": { weight_limit: 0, str_scaling: "none", equipment_slots: false, enable_group_inventory: true, enable_location_storage: true },
      "modern": { weight_limit: 0, equipment_slots: false, contextual_items: true, enable_location_storage: true },
    },
    stateVariables: ["player_inventory", "party_inventory", "group_inventory", "stash_inventory", "location_storages", "_base_weight_limit", "_effective_weight_limit", "_carry_bonuses", "_active_modifiers", "_encumbrance_percent", "_equipped_slots"],
  },
  configSchema: [
    { key: "weight_limit", label: "Base Weight Limit (0 = unlimited)", type: "number", default: 0, description: "Base carrying capacity. Set to 0 for no weight limit — items are tracked narratively." },
    { key: "contextual_items", label: "Contextual Items", type: "boolean", default: true, description: "Items are narrative objects. Stats like damage and weight are optional — the AI decides their impact contextually." },
    { key: "custom_tags", label: "Custom Item Tags", type: "text", default: "valuable, fragile, illegal, magical, consumable, quest, sentimental, heavy, light, concealed, perishable", description: "Comma-separated tags available for items. AI can also create new tags dynamically." },
    {
      key: "str_scaling", label: "Strength Scaling", type: "select",
      options: [
        { value: "none", label: "None — fixed or no limit", description: "Fixed weight limit or unlimited." },
        { value: "additive", label: "Additive — base + STR×5", description: "Scales linearly with strength." },
        { value: "multiplicative", label: "Multiplicative — base × (1 + STR×0.1)", description: "Rewards high STR." },
        { value: "dnd_style", label: "D&D Style — STR score × 15", description: "Classic D&D encumbrance." },
      ],
      default: "none",
    },
    { key: "str_stat_name", label: "Strength Stat Name", type: "text", default: "STR", description: "Which stat governs carrying capacity.", condition: { key: "str_scaling", value: "none" } },
    { key: "categorized", label: "Use Categories", type: "boolean", default: true, description: "Organize items into categories." },
    { key: "equipment_slots", label: "Equipment Slots", type: "boolean", default: true, description: "Enable equip/unequip system with stat modifiers." },
    { key: "slot_list", label: "Available Equipment Slots", type: "text", default: "weapon_main,weapon_off,armor,helmet,shield,ring,amulet,back,belt,boots", description: "Comma-separated equipment slot names.", condition: { key: "equipment_slots", value: true } },
    { key: "item_modifiers", label: "Item Stat Modifiers", type: "boolean", default: false, description: "Items can have explicit stat modifiers (optional — contextual mode lets AI decide)." },
    { key: "item_effects", label: "Item Passive Effects", type: "boolean", default: true, description: "Items can have passive text effects." },
    { key: "durability", label: "Item Durability", type: "boolean", default: false, description: "Items degrade with use." },
    { key: "cursed_items", label: "Cursed Items", type: "boolean", default: false, description: "Allow cursed items." },
    { key: "set_bonuses", label: "Item Set Bonuses", type: "boolean", default: false, description: "Items can belong to sets with bonuses." },
    { key: "enable_party_inventory", label: "Party / Shared Inventory", type: "boolean", default: false, description: "Shared party inventory accessible by all members." },
    { key: "party_weight_limit", label: "Party Inventory Weight Limit (0 = unlimited)", type: "number", default: 0, description: "Weight limit for shared party inventory.", condition: { key: "enable_party_inventory", value: true } },
    { key: "enable_group_inventory", label: "Group Inventory (Org/Faction)", type: "boolean", default: false, description: "Collective inventory for your organization, faction, or group. Covers armory, warehouse, treasury items." },
    { key: "group_weight_limit", label: "Group Storage Limit (0 = unlimited)", type: "number", default: 0, description: "Storage capacity for the group inventory.", condition: { key: "enable_group_inventory", value: true } },
    { key: "enable_stash", label: "Personal Stash", type: "boolean", default: false, description: "Location-locked personal stash." },
    { key: "enable_location_storage", label: "Location-Based Storage", type: "boolean", default: false, description: "Create storage at any location (bases, warehouses, vehicles). AI assigns metadata contextually." },
    { key: "location_items_merge", label: "Auto-Merge Location Items to Group", type: "boolean", default: false, description: "When enabled, location items from the scenario are automatically merged into the group inventory at adventure start. Otherwise they stay at the location.", condition: { key: "enable_location_storage", value: true } },
  ],
  systemPromptSection: (config) => {
    const limit = config.weight_limit || 0;
    const noLimit = limit === 0;
    const scaling = config.str_scaling || "none";
    const categorized = config.categorized !== false;
    const equipSlots = config.equipment_slots !== false;
    const slots = (config.slot_list || "weapon_main,weapon_off,armor,helmet,shield,ring,amulet,back,belt,boots").split(",").map((s: string) => s.trim());
    const modifiers = config.item_modifiers === true;
    const effects = config.item_effects !== false;
    const durability = config.durability === true;
    const cursed = config.cursed_items === true;
    const sets = config.set_bonuses === true;
    const strStat = config.str_stat_name || "STR";
    const hasParty = config.enable_party_inventory === true;
    const hasGroup = config.enable_group_inventory === true;
    const hasStash = config.enable_stash === true;
    const hasLocationStorage = config.enable_location_storage === true;
    const locationMerge = config.location_items_merge === true;
    const contextual = config.contextual_items !== false;
    const customTags = config.custom_tags || "valuable, fragile, illegal, magical, consumable, quest, sentimental, heavy, light, concealed, perishable";

    const scalingDesc: Record<string, string> = {
      none: noLimit ? "No weight limit — items tracked narratively." : `Fixed at ${limit} units.`,
      additive: `Base ${limit} + (${strStat} modifier x 5).`,
      multiplicative: `Base ${limit} x (1 + ${strStat}_modifier x 0.1).`,
      dnd_style: `${strStat} score x 15. Ignores base limit.`,
    };

    const scopeSections: string[] = [];
    if (hasParty) {
      const pl = config.party_weight_limit || 0;
      scopeSections.push(`### PARTY INVENTORY (Shared)\n- State: party_inventory. ${pl === 0 ? "No weight limit." : `Weight limit: ${pl}.`} Use manage_inventory with inventory_scope="party".`);
    }
    if (hasGroup) {
      const gl = config.group_weight_limit || 0;
      scopeSections.push(`### GROUP INVENTORY (Organization / Faction)\n- State: group_inventory. ${gl === 0 ? "No weight limit." : `Weight limit: ${gl}.`} Use manage_inventory with inventory_scope="group".\n- Covers all collective assets: armory, warehouse, treasury items, faction resources.\n- Cross-compatible with Strategy & Operations mechanic.`);
    }
    if (hasStash) scopeSections.push(`### PERSONAL STASH\n- State: stash_inventory. No weight limit. Use manage_inventory with inventory_scope="stash". Player must be at stash location.`);
    if (hasLocationStorage) scopeSections.push(`### LOCATION-BASED STORAGE
- State: location_storages (array of {location_id, location_name, items[], metadata}). Use manage_inventory with inventory_scope="location" and storage_location_id/storage_location_name.
- ANY location entity can become a storage point (base, warehouse, vehicle, safe house, ship cargo hold).
- Creating storage at a location means creating or referencing a CHILD LOCATION ENTITY.
- The AI decides what metadata the storage needs: capacity, security_level, accessibility, description, etc.
${locationMerge ? "- **AUTO-MERGE**: Location items from the scenario/world are automatically merged into the group inventory at adventure start." : "- Location items remain at the location and must be visited to access."}

### PHYSICAL COMMODITIES IN INVENTORY
- Physical goods (gold bars, salt, ammunition, rations, lumber, ore) should be INVENTORY items with \\\`quantity\\\`, NOT resource_pools.
- resource_pools are for ABSTRACT metrics only: morale, influence, manpower_capacity, political_capital.
- **CURRENCIES ARE INVENTORY ITEMS**: coins, credits, cash — tracked with quantity and "currency" tag.
- Example: "10 bars of gold" → manage_inventory add { item_name: "Gold Bar", quantity: 10, tags: ["valuable", "currency"] }
- Example: "50 units of salt" → manage_inventory add { item_name: "Salt", quantity: 50, item_category: "supply", tags: ["trade_good"] }`);

    scopeSections.push(`### INVENTORY INITIALIZATION (Player + Scenario)
- Player inventory is initialized ADDITIVELY from TWO sources:
  1. **Player Character** items defined at character creation.
  2. **Scenario** starting items defined by the adventure scenario.
- Both are MERGED into the player's inventory at game start. No duplicates — quantities are summed.
- Location/group items from the scenario go into their respective scopes (group or location).`);

    const anyMultiScope = hasParty || hasGroup || hasStash || hasLocationStorage;

    return `## INVENTORY (${contextual ? "Contextual" : "Hard"} Mechanic)
${noLimit && scaling === "none" ? "- NO weight limit. Items are tracked for narrative context, not encumbrance." : `- Base weight limit: ${limit} units. Scaling: ${scalingDesc[scaling]}`}
- ${categorized ? "Items organized by category and tags." : "Flat item list with tags."}
- Use manage_inventory tool for ALL item changes. Specify inventory_scope to target the right inventory (default: "player"). Use "npc" scope with target_entity_name for NPC inventories.
${contextual ? `
### CONTEXTUAL ITEMS
- Items are NARRATIVE objects. Hard stats (damage, weight, armor) are OPTIONAL.
- A "rusty knife" is dangerous in narrative without needing DMG=4. A "silk rope" is useful without needing weight=2.
- When adding items, provide: name, description, tags, and category. Only add hard stats if the game system requires them.
- Tags drive behavior: ${customTags}
- The AI may create new tags dynamically based on context (e.g., "poisoned", "blessed", "stolen").
- Item descriptions should capture narrative significance, not just stats.
` : `- EVERY item MUST have weight >= 0.5.`}

### INVENTORY vs. CODEX BOUNDARY
- Inventory is for PHYSICAL OBJECTS only: weapons, armor, tools, consumables, currency, trade goods, quest items.
- Knowledge discoveries (spells learned, lore, research, history, geography) go to the CODEX via record_lore_discovery — NOT inventory.
- If the player finds a physical book → inventory item. If they READ the book and learn something → record_lore_discovery.
- An artifact → inventory. The legend behind the artifact → codex.

### NPC INVENTORY
- Use \\\`inventory_scope: "npc"\\\` with \\\`target_entity_name\\\` to manage an NPC's carried items.
- This supports trading, looting, pickpocketing, and gifting.
- When an NPC is first inspected or interacted with for items, create their inventory with sensible items based on their description and role.
- NPC inventories persist on the entity's properties and are visible through entity inspection.

### NARRATIVE-DRIVEN INVENTORY (CRITICAL — ZERO EXCEPTIONS)
You MUST call manage_inventory for ANY item change implied by the narrative — regardless of the player's language or phrasing.
The Action Effect Classifier detects item changes semantically (language-agnostic) and will flag mandatory tool calls.

**ACQUISITION** → manage_inventory(action:"add"):
- Player acquires, receives, picks up, buys, loots, finds, or is given any physical object.

**REMOVAL** → manage_inventory(action:"remove" or "drop"):
- Player gives away, sells, drops, loses, discards, destroys, or has items taken.
- Transfers between scopes require TWO calls: remove from source, add to target.

**TRANSFER INFERENCE**: Infer scope from context:
- "org / faction / armory / warehouse / HQ / treasury" → inventory_scope:"group"
- "party / shared / team" → inventory_scope:"party"
- "stash / personal storage / safe" → inventory_scope:"stash"

**AMBIGUITY RULE**: If unclear whether the player acquired something, DEFAULT to adding it.

**NEVER narrate item changes without the corresponding tool call.**

- To TRANSFER between scopes: "remove" from source, "add" to target.
${anyMultiScope ? `\n### MULTI-SCOPE INVENTORY\nScopes: player, npc${hasParty ? ", party" : ""}${hasGroup ? ", group" : ""}${hasStash ? ", stash" : ""}${hasLocationStorage ? ", location" : ""}` : ""}
${equipSlots ? `\n### EQUIPMENT SYSTEM\n- Slots: ${slots.join(", ")}. Use action "equip"/"unequip". Equipped items provide modifiers.` : ""}
${modifiers ? `\n### ITEM STAT MODIFIERS\n- Items have "modifiers" array: {stat, value, type("flat"|"percent"), description}. Active only when equipped.` : ""}
${effects ? `\n### ITEM PASSIVE EFFECTS\n- Items have "effects" string array. You enforce them narratively.` : ""}
${durability ? `\n### DURABILITY\n- Track 0-100. At 25%: Damaged. At 0%: Broken.` : ""}
${cursed ? `\n### CURSED ITEMS\n- Can't be unequipped without magic. Pair strong bonus with drawback.` : ""}
${sets ? `\n### ITEM SETS\n- Items belong to sets. 2+ pieces = set bonuses.` : ""}
${!noLimit ? `\n### ENCUMBRANCE (Soft Limit)\n- <100%: Normal. 100-125%: ENCUMBERED. 125-150%: HEAVILY ENCUMBERED. 150%+: OVERBURDENED.` : ""}
${scopeSections.join("\n\n")}`;
  },
  widgets: [
    {
      id: "inventory_tab",
      type: "sidebar_tab",
      label: "Inventory",
      icon: "Backpack",
      component: "InventoryPanel",
      dataBindings: {
        items: "playerChar.inventory",
        weightLimit: "variables._base_weight_limit",
        effectiveWeightLimit: "variables._effective_weight_limit",
        encumbrancePercent: "variables._encumbrance_percent",
        carryBonuses: "variables._carry_bonuses",
        activeModifiers: "variables._active_modifiers",
        partyInventory: "variables.party_inventory",
        groupInventory: "variables.group_inventory",
        stashInventory: "variables.stash_inventory",
        locationStorages: "variables.location_storages",
        partyWeightLimit: "variables._party_weight_limit",
        groupWeightLimit: "variables._group_weight_limit",
      },
      visibility: "when_active",
      priority: 30,
      actions: [
        { id: "use_item", label: "Use", icon: "Wand2", messageTemplate: "[USE_ITEM: {{item_name}}]" },
        { id: "drop_item", label: "Drop", icon: "Trash2", messageTemplate: "[DROP_ITEM: {{item_name}}]" },
        { id: "equip_item", label: "Equip", icon: "Shield", messageTemplate: "[EQUIP_ITEM: {{item_name}}]" },
        { id: "unequip_item", label: "Unequip", icon: "ShieldOff", messageTemplate: "[UNEQUIP_ITEM: {{item_name}}]" },
        { id: "inspect_item", label: "Inspect", icon: "Eye", messageTemplate: "I examine my {{item_name}} closely." },
        { id: "transfer_to_party", label: "To Party", icon: "Users", messageTemplate: "[TRANSFER_TO_PARTY: {{item_name}}]" },
        { id: "transfer_to_stash", label: "To Stash", icon: "Archive", messageTemplate: "[TRANSFER_TO_STASH: {{item_name}}]" },
        { id: "store_at_location", label: "Store Here", icon: "Warehouse", messageTemplate: "[STORE_AT_LOCATION: {{item_name}}]" },
      ],
    },
  ],
  initializeState: (config) => {
    const limit = config.weight_limit || 0;
    const base: Record<string, any> = {
      player_inventory: [],
      _base_weight_limit: limit,
      _effective_weight_limit: limit,
      _carry_bonuses: [],
      _active_modifiers: [],
      _encumbrance_percent: 0,
      _equipped_slots: {},
    };
    if (config.enable_party_inventory) { base.party_inventory = []; base._party_weight_limit = config.party_weight_limit || 0; }
    if (config.enable_group_inventory) { base.group_inventory = []; base._group_weight_limit = config.group_weight_limit || 0; }
    if (config.enable_stash) { base.stash_inventory = []; }
    if (config.enable_location_storage) { base.location_storages = []; }
    return base;
  },
  onEvent: (event) => {
    if (event.type === "item_acquired") return { narrativeNotes: [`🎒 Item acquired: ${event.data.item_name}`] };
    if (event.type === "stat_changed" && event.data.stat) return { narrativeNotes: [`🎒 Stat change — carry capacity may need recalculation.`] };
    if (event.type === "org_requisition" && event.data.item_name) return { narrativeNotes: [`🎒 Organization requisition: ${event.data.item_name}`] };
    if (event.type === "location_storage_created") return { narrativeNotes: [`📦 New storage established at ${event.data.location_name}`] };
    return null;
  },
  postTurnHook: (context) => {
    const config = context.config;
    const vars = context.state.variables || {};
    const playerChar = context.state.player_character || {};
    const inventory = playerChar.inventory || [];

    const baseLimit = config.weight_limit || 0;
    const noLimit = baseLimit === 0;
    const scaling = config.str_scaling || "none";
    const strStat = (config.str_stat_name || "STR").toLowerCase();

    // If no weight limit and no scaling, skip encumbrance entirely
    if (noLimit && scaling === "none") {
      return {
        stateUpdates: {
          _base_weight_limit: 0,
          _effective_weight_limit: 0,
          _encumbrance_percent: 0,
        },
      };
    }

    const stats = playerChar.stats || vars.player_stats || {};
    let strScore = 10;
    let strMod = 0;
    for (const [key, val] of Object.entries(stats)) {
      if (key.toLowerCase() === strStat || key.toLowerCase().startsWith(strStat)) {
        strScore = typeof val === "number" ? val : 10;
        strMod = Math.floor((strScore - 10) / 2);
        break;
      }
    }

    let charLimit = baseLimit;
    if (scaling === "additive") charLimit = baseLimit + (strMod * 5);
    else if (scaling === "multiplicative") charLimit = Math.round(baseLimit * (1 + strMod * 0.1));
    else if (scaling === "dnd_style") charLimit = strScore * 15;

    const carryBonuses: { source: string; value: number; type?: string }[] = [];
    const activeModifiers: { stat: string; value: number; source: string }[] = [];

    if (scaling !== "none" && strMod !== 0) {
      carryBonuses.push({ source: `${strStat.toUpperCase()} ${strMod >= 0 ? "+" : ""}${strMod}`, value: charLimit - baseLimit });
    }

    for (const item of inventory) {
      if (typeof item !== "object" || !item.equipped || !item.modifiers) continue;
      for (const mod of item.modifiers) {
        if (mod.stat === "carry_capacity" || mod.stat === "weight_limit") {
          if (mod.type === "percent") {
            const bonus = Math.round(charLimit * (mod.value / 100));
            carryBonuses.push({ source: item.name, value: bonus, type: "percent" });
            charLimit += bonus;
          } else {
            carryBonuses.push({ source: item.name, value: mod.value });
            charLimit += mod.value;
          }
        } else {
          activeModifiers.push({ stat: mod.stat, value: mod.value, source: item.name });
        }
      }
    }

    const totalWeight = inventory.reduce((sum: number, item: any) => {
      if (typeof item === "string") return sum;
      return sum + ((item.weight || 0) * (item.quantity || 1));
    }, 0);
    const encPct = charLimit > 0 ? Math.round((totalWeight / charLimit) * 100) : 0;

    const notes: string[] = [];
    if (encPct >= 100) notes.push(`⚠ ENCUMBERED (${encPct}%): ${totalWeight}/${charLimit} weight.`);

    return {
      stateUpdates: {
        _base_weight_limit: baseLimit,
        _effective_weight_limit: charLimit,
        _carry_bonuses: carryBonuses,
        _active_modifiers: activeModifiers,
        _encumbrance_percent: encPct,
      },
      narrativeNotes: notes.length > 0 ? notes : undefined,
    };
  },
  tools: [
    {
      type: "function",
      function: {
        name: "manage_inventory",
        description: "Add, remove, use, equip, or unequip an item. Supports multiple inventory scopes: player (default), party, group (org/faction), stash, location, npc. Items are contextual — hard stats are optional. Currencies are inventory items. Use 'npc' scope with target_entity_name to manage NPC inventories for trading, looting, pickpocketing, and gifting.",
        parameters: {
          type: "object",
          properties: {
            action: { type: "string", enum: ["add", "remove", "use", "drop", "equip", "unequip", "create_storage"] },
            inventory_scope: { type: "string", enum: ["player", "party", "group", "stash", "location", "npc"], description: "Which inventory to target. Default: player. 'group' covers org/faction assets. 'npc' targets a specific entity." },
            target_entity_id: { type: "string", description: "Entity ID of the NPC (for npc scope). Use target_entity_name if ID is unknown." },
            target_entity_name: { type: "string", description: "Name of the NPC whose inventory to manage (for npc scope). Fuzzy matched." },
            storage_location_id: { type: "string", description: "Entity ID of the location for location-scope storage." },
            storage_location_name: { type: "string", description: "Name of the location for location-scope storage (used when creating new storage)." },
            storage_metadata: { type: "object", description: "AI-determined metadata for new storage locations (e.g., capacity, security_level, access_restrictions, temperature, description)." },
            item_name: { type: "string" },
            item_weight: { type: "number", description: "Weight (optional — 0 or omit for contextual items with no weight tracking)." },
            item_category: { type: "string", description: "Category: weapon, armor, consumable, quest_item, accessory, tool, document, vehicle, supply, currency, artifact, or any custom category." },
            item_tags: { type: "array", items: { type: "string" }, description: "Narrative tags: valuable, fragile, illegal, magical, consumable, quest, sentimental, concealed, perishable, or any custom tag." },
            item_rarity: { type: "string", enum: ["common", "uncommon", "rare", "epic", "legendary"] },
            item_description: { type: "string", description: "Narrative description of the item — its appearance, significance, or purpose." },
            equip_slot: { type: "string", description: "Equipment slot (player scope only)" },
            modifiers: { type: "array", items: { type: "object", properties: { stat: { type: "string" }, value: { type: "number" }, type: { type: "string", enum: ["flat", "percent"] }, description: { type: "string" } }, required: ["stat", "value"] } },
            effects: { type: "array", items: { type: "string" } },
            durability: { type: "number" },
            charges: { type: "number" },
            max_charges: { type: "number" },
            is_quest_item: { type: "boolean" },
            is_cursed: { type: "boolean" },
            set_id: { type: "string" },
            quantity: { type: "number" },
            reason: { type: "string" },
          },
          required: ["action", "item_name"],
        },
      },
    },
  ],
};
